import httpClient from "@plugins/http";
import { ApiResponse } from "@apis/api.ts";
import {
  StudentGroup,
  StudentGroupActiveGroup,
  StudentGroupApiModel,
  StudentGroupBasicData,
  StudentGroupMemberListingModel,
  YearSchoolModel,
} from "@modules/student-group/student-group-types.ts";
import { DataTableOutput } from "@plugins/common/types.ts";
import { ClassListingStudentModel } from "@modules/study-buddy/types.ts";

export interface ActiveStudentGroupResponse {
  schoolId: number;
  academicYear: number;
  formClass: string[];
  studentGroup: StudentGroupActiveGroup[];
}

/**
 * Student group apis
 */
const fetchMyActiveStudentGroups = (params): Promise<ApiResponse<ActiveStudentGroupResponse>> => {
  return httpClient.get(`/apis/studentgroup/list/active`, { params: params });
};

const fetchPinnedStudentGroups = () => {
  return httpClient.get(`/apis/studentgroup/list/pinned-classgroup`);
};

const fetchHomeStudentGroups = (): Promise<ApiResponse<StudentGroupBasicData[]>> => {
  return httpClient.get(`/apis/studentgroup/list/classgroup-home`);
};

const fetchStudentGroupMetadata = (collectionUuid) => {
  return httpClient.get(`/apis/studentgroup/metadata/${collectionUuid}`);
};

const pinStudentGroup = (collectionUuid) => {
  return httpClient.post(`/apis/studentgroup/pin/${collectionUuid}`);
};

const unpinStudentGroup = (collectionUuid) => {
  return httpClient.post(`/apis/studentgroup/unpin/${collectionUuid}`);
};
const fetchStudentGroupSummary = (studentGroupUuid) => {
  return httpClient.get(`/apis/studentgroup/summary/${studentGroupUuid}`);
};

const fetchStudentGroupStudent = async (
  collectionUuid: string,
): Promise<DataTableOutput<ClassListingStudentModel>> => {
  return await httpClient.get(`/apis/studentgroup/students/${collectionUuid}`);
};

const fetchStudentGroupStudentNames = (studentGroupUuid) => {
  return httpClient.get(`/apis/studentgroup/student/names/${studentGroupUuid}`);
};

const fetchStudentGroupTeacher = (collectionUuid) => {
  return httpClient.get(`/apis/studentgroup/teachers/${collectionUuid}`);
};

const setStudentOtp = (params) => {
  return httpClient.post(`/apis/studentgroup/administration/2htp`, params);
};

const getCurrentTeacher = () => {
  return httpClient.get(`/apis/studentgroup/current-teacher`);
};

interface NonStaffStudentGroupInputModel {
  subjectId: number;
  levelAndStreamId: number;
  academicYear?: number;
  userId?: number;
}

const fetchNonStaffStudentGroups = (
  subjectId: number,
  levelAndStreamId: number,
  academicYear?: number,
  userId?: number,
): Promise<ApiResponse<StudentGroupApiModel[]>> => {
  const data: NonStaffStudentGroupInputModel = {
    subjectId: subjectId,
    levelAndStreamId: levelAndStreamId,
  };
  if (academicYear) {
    data.academicYear = academicYear;
  }
  if (userId) {
    data.userId = userId;
  }
  return httpClient.get(`/apis/studentgroup/non-staff`, {
    params: data,
  });
};

// Listing Api

const fetchStudentGroupList = async (requestData): Promise<ApiResponse<StudentGroup[]>> => {
  return await httpClient.get(`/apis/studentgroup/list`, {
    params: requestData,
  });
};

const fetchYearSchools = async (): Promise<ApiResponse<YearSchoolModel>> => {
  return await httpClient.get(`/apis/studentgroup/list/year-school`);
};

// TODO: [API] remove url variable
const checkStudentGroupAssignmentAvailability = (studGrpIds) => {
  const params = {
    collectionIds: studGrpIds,
  };
  return httpClient.get("/apis/studentgroup/get-assignment-availability", {
    params: params,
  });
};

//app listing apis
const fetchStudentGroupUserInfo = (studentGroupCollectionUuid) => {
  return httpClient.get(`/apis/studentgroup/permissions/${studentGroupCollectionUuid}`);
};

const fetchStudentGroupAvailableApps = (studentGroupCollectionUuid) => {
  return httpClient.get(`/apis/studentgroup/application/available/${studentGroupCollectionUuid}`);
};

const fetchStudentGroupInstalledApps = (studentGroupCollectionUuid) => {
  return httpClient.get(`/apis/studentgroup/application/installed/${studentGroupCollectionUuid}`);
};

// TODO: [API] split it to another api instead? i.e.
// - class.group.api
// - class.group.teaching.group.api
// - class.group.subject.group.api
// - class.group.subgroup.api

/**
 * Teaching group apis
 */
const searchStudent = (params) => {
  return httpClient.post(`/apis/teaching-group/users/search`, params);
};

const createTeachingGroup = (params) => {
  return httpClient.post(`/apis/teaching-group/create`, params, {
    headers: {
      "X-With-Richtext": true,
    },
  });
};

const saveTeachingGroup = (params) => {
  return httpClient.post(`/apis/teaching-group/save`, params, {
    headers: {
      "X-With-Richtext": true,
    },
  });
};

const saveSubjectGroup = (params) => {
  return httpClient.post(`/apis/subject-group/save`, params, {
    headers: {
      "X-With-Richtext": true,
    },
  });
};

const archiveTeachingGroup = (uuid) => {
  return httpClient.post(`/apis/studentgroup/archive/${uuid}`);
};

const getTeacherStudentAccount = (params) => {
  return httpClient.post(`/apis/teaching-group/teacherassociateaccount`, params);
};

const getTeacherUserIdFromTeacherStudentUserId = (params) => {
  return httpClient.post(`/apis/teaching-group/teacheraccount`, params);
};

const fetchActiveTeachingGroups = () => {
  return httpClient.get(`/apis/teaching-group/list/own-by-me`);
};

const fetchStudentGroupAllStudents = (
  studentGroupUuid: string,
): Promise<ApiResponse<ClassListingStudentModel[]>> => {
  return httpClient.get(`/apis/studentgroup/students/all/${studentGroupUuid}`);
};

const fetchAllStudentsByStudentGroups = (
  studentGroupUuids: string[],
): Promise<ApiResponse<Record<string, ClassListingStudentModel[]>>> => {
  return httpClient.get(`/apis/all/studentgroup/students`, {
    params: { studentGroupUuids: studentGroupUuids },
  });
};

const fetchStudentGroupStudentCount = async (
  studentGroupCollectionUuid,
): Promise<ApiResponse<number>> => {
  return await httpClient.get(`/apis/studentgroup/studentcount/${studentGroupCollectionUuid}`);
};

const fetchStudentGroupTeacherCount = (studentGroupCollectionUuid) => {
  return httpClient.get(`/apis/studentgroup/teachercount/${studentGroupCollectionUuid}`);
};
const fetchStudentGroupAvailableAssignmentAcademicYearList = (studentGroupCollectionId) => {
  return httpClient.get(`/apis/studentgroup/assignment-academic-year/list`, {
    params: { studentGroupCollectionId: studentGroupCollectionId },
  });
};

const uploadAttempt = (params) => {
  return httpClient.post("/apis/studentgroup/attempt/upload", params.fileEntry, {
    params: {
      studentGroupUuid: params.studentGroupUuid,
    },
  });
};

const checkHasAssociatedAccountInStudentGroup = (studentGroupCollectionUuid) => {
  return httpClient.get(`/apis/studentgroup/has-associated-account/${studentGroupCollectionUuid}`);
};

const getStudentGroupNotifications = (studentGroupCollectionUuid) => {
  return httpClient.get(`/apis/studentgroup/notifications/${studentGroupCollectionUuid}`);
};

const restoreStudentGroup = (studentGroupUuid) => {
  return httpClient.post(`/apis/studentgroup/restore-group/${studentGroupUuid}`);
};

const hideStudentGroup = (studentGroupUuid: string) => {
  return httpClient.post(`/apis/studentgroup/hide/${studentGroupUuid}`);
};

const unhideStudentGroup = (studentGroupUuid: string) => {
  return httpClient.post(`/apis/studentgroup/unhide/${studentGroupUuid}`);
};

const getStudentGroupRequest = (studentGroupUuid) => {
  return httpClient.get(`/apis/student-group-membership/is-requested/${studentGroupUuid}`);
};

const createPendingStudentGroupAccessRequest = (studentGroupUuid) => {
  return httpClient.post(`/apis/student-group-membership/request/${studentGroupUuid}`);
};

const getTeacherPendingApprovalCount = (studentGroupUuid) => {
  return httpClient.get(
    `/apis/student-group-membership/get-teacher-pending-count/${studentGroupUuid}`,
  );
};

const getStudentPendingApprovalCount = (studentGroupUuid) => {
  return httpClient.get(
    `/apis/student-group-membership/get-student-pending-count/${studentGroupUuid}`,
  );
};

const getTeacherPendingRequestList = (studentGroupUuid, start, length) => {
  return httpClient.get(
    `/apis/student-group-membership/get-teacher-pending-list/${studentGroupUuid}`,
    {
      params: {
        start: start,
        length: length,
      },
    },
  );
};

const getStudentPendingRequestList = (studentGroupUuid, start, length) => {
  return httpClient.get(
    `/apis/student-group-membership/get-student-pending-list/${studentGroupUuid}`,
    {
      params: {
        start: start,
        length: length,
      },
    },
  );
};

const approveStudentGroupAccessRequest = (studentGroupUuid, approvingRole, userIds) => {
  return httpClient.post(
    `/apis/student-group-membership/approve/${approvingRole}/${studentGroupUuid}`,
    userIds,
  );
};

const rejectStudentGroupAccessRequest = (studentGroupUuid, rejectingRole, userIds) => {
  return httpClient.post(
    `/apis/student-group-membership/reject/${rejectingRole}/${studentGroupUuid}`,
    userIds,
  );
};

const searchStudentGroup = (params) => {
  return httpClient.get(`/apis/studentgroup/search`, {
    params: params,
  });
};

const searchStudentGroupListing = (data) => {
  return httpClient.get("/apis/studentgroup/list/search", { params: data });
};

const isStudentGroupCommunity = (studentGroupCollectionUuid) => {
  return httpClient.get(`/apis/studentgroup/is-community/${studentGroupCollectionUuid}`);
};

const fetchStudentGroupMembers = (
  studentGroupUuid: string,
): Promise<ApiResponse<StudentGroupMemberListingModel>> => {
  return httpClient.get(`/apis/studentgroup/members/${studentGroupUuid}`);
};

export default {
  fetchMyActiveStudentGroups,
  fetchPinnedStudentGroups,
  fetchHomeStudentGroups,
  fetchStudentGroupMetadata,
  pinStudentGroup,
  unpinStudentGroup,
  fetchStudentGroupSummary,
  fetchStudentGroupStudent,
  fetchStudentGroupTeacher,
  searchStudent,
  createTeachingGroup,
  saveTeachingGroup,
  saveSubjectGroup,
  archiveTeachingGroup,
  getTeacherUserIdFromTeacherStudentUserId,
  getTeacherStudentAccount,
  setStudentOtp,
  getCurrentTeacher,
  fetchNonStaffStudentGroups,
  fetchActiveTeachingGroups,
  fetchYearSchools,
  checkStudentGroupAssignmentAvailability,
  fetchStudentGroupUserInfo,
  fetchStudentGroupAvailableApps,
  fetchStudentGroupInstalledApps,
  fetchStudentGroupAllStudents,
  fetchAllStudentsByStudentGroups,
  fetchStudentGroupList,
  fetchStudentGroupStudentCount,
  fetchStudentGroupTeacherCount,
  fetchStudentGroupStudentNames,
  fetchStudentGroupAvailableAssignmentAcademicYearList,
  uploadAttempt,
  checkHasAssociatedAccountInStudentGroup,
  getStudentGroupNotifications,
  restoreStudentGroup,
  hideStudentGroup,
  unhideStudentGroup,
  getStudentGroupRequest,
  createPendingStudentGroupAccessRequest,
  getTeacherPendingApprovalCount,
  getStudentPendingApprovalCount,
  getTeacherPendingRequestList,
  getStudentPendingRequestList,
  approveStudentGroupAccessRequest,
  rejectStudentGroupAccessRequest,
  searchStudentGroup,
  searchStudentGroupListing,
  isStudentGroupCommunity,
  fetchStudentGroupMembers,
};
